/**
 * Document API Service
 * 
 * API calls for document upload and management
 */

import { apiService, ApiResponse } from './api';

// =============================================================================
// Types
// =============================================================================

// Bounding box type for field locations
export interface BoundingBox {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ExtractedFieldLocation {
    field_name: string;
    value: any;
    bbox: BoundingBox | null;
    confidence?: number;
    discrepancy_type?: string;
}

// GPT Detailed Analysis (Turkish)
export interface DetailedAnalysis {
    overall_assessment?: string;       // Genel Değerlendirme
    discrepancies_explanation?: string; // Uyuşmazlık Açıklaması
    recommendations?: string;           // Öneriler
}

// Verification Summary Stats
export interface VerificationSummary {
    matches?: number;
    price_mismatches?: number;
    quantity_mismatches?: number;
    total_items?: number;
    overall_match?: boolean;
}

// Reconciliation Line Item
export interface ReconciliationLine {
    item_description?: string;
    description?: string;
    invoice_item?: {
        description?: string;
        price?: number | string;
        quantity?: number;
    };
    quote_item?: {
        description?: string;
        price?: number | string;
        quantity?: number;
    };
    invoice_value?: number | string;
    quote_value?: number | string;
    quantity?: number;
    status: 'match' | 'mismatch' | 'price_mismatch' | 'quantity_mismatch' | 'missing';
    reason?: string;
    bbox?: BoundingBox | null;
}

// Multi-Document Detection
export interface DocumentBoundary {
    page: number;
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
    confidence: number;
    detection_method: string;
    pages?: number[];  // Phase 6: Multi-page document support
}

export interface MultiDocumentInfo {
    detected: boolean;
    document_count: number;
    confidence: number;
    detection_reason?: string;
    is_parent?: boolean;
    is_child?: boolean;
    boundaries?: DocumentBoundary[];
}

export interface DocumentItem {
    id: string;
    company_id: string;
    user_id: string;
    type: 'invoice' | 'quote';
    filename: string;
    file_size: number;
    ocr_status: 'pending' | 'processing' | 'completed' | 'failed';
    extraction_status?: 'pending' | 'completed' | 'failed';
    extraction_error?: string;
    extraction_retries?: number;
    extracted_text?: string;
    extracted_data?: {
        // Legacy nested format
        vendor?: { name?: string; tax_id?: string; address?: string };
        financials?: { total_amount?: number; currency?: string; tax?: number; subtotal?: number };
        line_items?: any[];
        field_locations?: ExtractedFieldLocation[];

        // Document AI flat format
        supplier_name?: string;
        supplier_tax_id?: string;
        supplier_address?: string;
        total_amount?: number;
        total_tax_amount?: number;
        net_amount?: number;
        currency?: string;
        items?: any[];
        entities_with_bounds?: any[];

        // Common fields
        invoice_number?: string;
        invoice_date?: string;
        due_date?: string;

        // Allow additional fields
        [key: string]: any;
    };
    comparison?: {
        linked_quote_id?: string;
        status?: string;
        match_score?: number;
        verification?: any; // kept for legacy
        verification_result?: {
            reconciliation_lines?: ReconciliationLine[];
            detailed_analysis?: DetailedAnalysis;
            summary?: VerificationSummary;
            [key: string]: any;
        };
    };
    expense_category?: string;
    // Multi-document support
    parent_document_id?: string;
    split_index?: number;
    multi_document?: MultiDocumentInfo;
    child_document_ids?: string[];
    ocr_error?: string;
    created_at: string;
    updated_at: string;
}

export interface DocumentStatus {
    id: string;
    ocr_status: 'pending' | 'processing' | 'completed' | 'failed';
    ocr_error?: string;
}

export interface DocumentListResponse {
    documents: DocumentItem[];
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_prev: boolean;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Upload a document
 */
export const uploadDocument = async (
    file: File,
    type: 'invoice' | 'quote'
): Promise<ApiResponse<DocumentItem>> => {
    return apiService.upload<DocumentItem>('/api/documents/upload', file, { type });
};

/**
 * Get list of documents
 */
export const getDocuments = async (
    page: number = 1,
    pageSize: number = 20,
    type?: 'invoice' | 'quote',
    matchStatus?: 'matched' | 'unmatched'
): Promise<ApiResponse<DocumentListResponse>> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (type) params.type = type;
    if (matchStatus) params.match_status = matchStatus;
    return apiService.get<DocumentListResponse>('/api/documents', params);
};

/**
 * Get document by ID
 */
export const getDocument = async (
    documentId: string
): Promise<ApiResponse<DocumentItem>> => {
    return apiService.get<DocumentItem>(`/api/documents/${documentId}`);
};

/**
 * Get document status (for polling)
 */
export const getDocumentStatus = async (
    documentId: string
): Promise<ApiResponse<DocumentStatus>> => {
    return apiService.get<DocumentStatus>(`/api/documents/${documentId}/status`);
};

/**
 * Delete document
 */
export const deleteDocument = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(`/api/documents/${documentId}`);
};

/**
 * Download document file
 */
export const downloadDocument = async (documentId: string, filename: string): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5003';

    try {
        const response = await fetch(`${baseUrl}/api/documents/${documentId}/download`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error('Download failed');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error('Download error:', error);
        throw error;
    }
};

// Expense category definitions
export const EXPENSE_CATEGORIES: Record<string, { tr: string; en: string }> = {
    food: { tr: 'Yiyecek ve İçecek', en: 'Food & Beverage' },
    fuel: { tr: 'Akaryakıt', en: 'Fuel' },
    accommodation: { tr: 'Konaklama', en: 'Accommodation' },
    transport: { tr: 'Ulaşım', en: 'Transportation' },
    toll: { tr: 'Otoyol/Köprü Geçiş', en: 'Toll' },
    parking: { tr: 'Otopark', en: 'Parking' },
    office_supplies: { tr: 'Kırtasiye/Ofis', en: 'Office Supplies' },
    communication: { tr: 'İletişim', en: 'Communication' },
    other: { tr: 'Diğer', en: 'Other' },
};

/**
 * Update expense category of a document
 */
export const updateExpenseCategory = async (
    documentId: string,
    expenseCategory: string
): Promise<ApiResponse<{ expense_category: string }>> => {
    return apiService.patch<{ expense_category: string }>(
        `/api/documents/${documentId}/expense-category`,
        { expense_category: expenseCategory }
    );
};

/**
 * Matching Analysis Response (includes job_id for WebSocket tracking)
 */
export interface MatchingAnalysisResponse extends ApiResponse<any[]> {
    job_id?: string; // For WebSocket progress tracking
}

/**
 * Run matching analysis
 */
export const analyzeMatching = async (
    invoiceIds: string[],
    quoteIds: string[]
): Promise<MatchingAnalysisResponse> => {
    return apiService.post<any[]>('/api/matching/analyze', {
        invoice_ids: invoiceIds,
        quote_ids: quoteIds
    }) as Promise<MatchingAnalysisResponse>;
};

/**
 * Unlink document
 */
export const unlinkDocument = async (
    invoiceId: string
): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/api/invoices/${invoiceId}/unlink`, {});
};

/**
 * Update extracted document fields
 */
export const updateDocumentFields = async (
    documentId: string,
    fields: Record<string, any>
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(
        `/api/documents/${documentId}/fields`,
        { fields }
    );
};

// =============================================================================
// Dashboard Stats Types & API
// =============================================================================

export interface DashboardCounts {
    total: number;
    matched: number;
    discrepancy: number;
    pending: number;
}

export interface SpendingDataPoint {
    date: string;
    total: number;
    count: number;
}

export interface CurrencySpending {
    currency: string;
    time_series: SpendingDataPoint[];
    currency_total: number;
}

export interface CategorySpending {
    category: string;
    currency: string;
    time_series: SpendingDataPoint[];
    category_total: number;
}

export interface DateRange {
    min_month: string | null;
    max_month: string | null;
}

export interface DashboardStatsResponse {
    counts: DashboardCounts;
    spending_by_date: CurrencySpending[];
    spending_by_category: CategorySpending[];
    date_range: DateRange;
    period: string;
    group_by: string;
}

/**
 * Get dashboard statistics (counts + spending charts)
 */
export const getDashboardStats = async (
    period: string = '30d',
    groupBy: string = 'day',
    startDate?: string,
    endDate?: string,
): Promise<ApiResponse<DashboardStatsResponse>> => {
    const params: Record<string, string> = { period, group_by: groupBy };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiService.get<DashboardStatsResponse>('/api/stats', params);
};

// Approximate exchange rates to TRY for chart normalization
export const CURRENCY_RATES_TO_TRY: Record<string, number> = {
    TRY: 1,
    CHF: 55,
    EUR: 50,
    USD: 45,
    GBP: 57,
};

// Export as object
const documentApi = {
    uploadDocument,
    getDocuments,
    getDocument,
    getDocumentStatus,
    deleteDocument,
    downloadDocument,
    analyzeMatching,
    unlinkDocument,
    updateExpenseCategory,
    updateDocumentFields,
    getDashboardStats,
};

export default documentApi;
